import { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { motion } from 'framer-motion'

import { Body } from '@foxino/components-web'
import { BookOpen01, Plus } from '@foxino/icons-react'

import { BugReportButton, BugReportDialog } from '@app/components'
import { ImageContent } from '@app/config/constants/ImageContent'
import { RouteType } from '@app/config/router/navMenu.types'
import { Pages } from '@app/config/router/PagesEnum'
import { useTestId } from '@app/hooks/useTestId'
import { useTranslation } from '@app/locales'
import { dialogStateStore } from '@app/modules/learningPlan/store/dialogStateStore'
import { GradientDivider } from '@app/modules/learningPlan/view/components/GradientDivider'

import { useNavigationPages } from '../../hooks/useNavigationPages'
import { useSideNavigationHover } from '../../hooks/useSideNavigationHover'
import { sideNavigationStore } from '../../store/sideNavigationStore'
import { checkUrlIsActive } from '../../utils/checkUrlIsActive'
import { FoxinoHeaderLogo } from '../NavigationHeader/FoxinoHeaderLogo'
import { ClassroomNavigationItem } from './components/ClassroomNavigationItem'
import { CollapsibleWrapper } from './components/CollapsibleWrapper'
import { NavigationItem } from './components/NavigationItem'
import { UserInfo } from './components/UserInfo'
import HoverableIcon from './hoverableIcon.svg?react'

const SIDE_NAVIGATION_WIDTH_COLLAPSED = 70
const SIDE_NAVIGATION_WIDTH_EXPANDED = 330

export const SideNavigation = () => {
  const { t } = useTranslation(['common', 'classroom'])
  const location = useLocation()
  const testId = useTestId(['side_navigation'])

  const navigationDiv = useRef<HTMLDivElement>(null)
  const classroomNavigationDiv = useRef<HTMLDivElement>(null)

  const { isHidden, onHoverStart, onHoverEnd } = useSideNavigationHover()
  const { topRoutes, middleRoutes, classroomRoutes } = useNavigationPages()

  const { isSideMenuExpanded, isBugReportOpen } = dialogStateStore.useStore((store) => ({
    isSideMenuExpanded: store.sideMenu.isOpen,
    isBugReportOpen: store.reportBug.isOpen
  }))
  const { isHovered } = sideNavigationStore.useStore(({ isHidden, isHovered }) => ({ isHidden, isHovered }))

  const newClassRoute: RouteType = useMemo(
    () => ({
      label: t('newClass.create'),
      to: Pages.NEW_CLASS,
      testId: testId('new_class'),
      Icon: Plus
    }),
    []
  )

  useEffect(() => {
    if (classroomNavigationDiv.current) {
      classroomNavigationDiv.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [location.pathname])

  return (
    <motion.div
      onHoverStart={onHoverStart}
      onHoverEnd={onHoverEnd}
      id="side-navigation"
      ref={navigationDiv}
      animate={
        isSideMenuExpanded
          ? { translateX: 0, width: SIDE_NAVIGATION_WIDTH_EXPANDED }
          : { translateX: isHidden ? (isHovered ? 0 : -80) : 0, width: SIDE_NAVIGATION_WIDTH_COLLAPSED }
      }
      initial={{ translateX: '-40vw', width: SIDE_NAVIGATION_WIDTH_COLLAPSED }}
      transition={{ duration: 0.1 }}
      className={classNames(
        'left-0 top-0 z-30 flex flex-col items-center border-primary-600 bg-gradient-to-r from-primary-1050 to-primary-1000 shadow-2xl',
        isHidden
          ? 'fixed top-20 h-[calc(100vh-160px)] -translate-y-1/2 rounded-r-2xl border border-l-0 border-primary-700 shadow-3xl'
          : 'sticky h-screen',
        { 'border-r-2': isSideMenuExpanded }
      )}
    >
      <div className={classNames('relative w-full py-4', isSideMenuExpanded && 'pl-6')}>
        {isSideMenuExpanded ? (
          <img src={ImageContent.logo} className="w-28" />
        ) : (
          <FoxinoHeaderLogo className="mx-auto" />
        )}
      </div>

      <aside className="relative z-10 flex h-full w-full flex-col overflow-hidden">
        <nav className="flex h-full flex-col justify-between">
          <div className="flex h-full flex-col justify-between">
            {/* Top routes */}
            <div id="side-navigation-scroll" className="no-scrollbar flex-1 overflow-y-auto px-[9px] pb-6 pt-2">
              <GradientDivider className="mb-3" />
              <div className="flex flex-col gap-2">
                {topRoutes.map((route, index) => (
                  <NavigationItem
                    {...route}
                    key={index}
                    isExpanded={isSideMenuExpanded}
                    isActive={checkUrlIsActive(route, location)}
                  />
                ))}

                {classroomRoutes.map((route, index) => {
                  const isActive = checkUrlIsActive(route, location)

                  return (
                    <div
                      key={index}
                      ref={isActive ? classroomNavigationDiv : undefined}
                      data-testid={testId('classroom.self', String(index))}
                    >
                      <ClassroomNavigationItem
                        {...route}
                        label={route.label}
                        identificator={route.identificator || ''}
                        key={index}
                        location={location}
                        isExpanded={isSideMenuExpanded}
                        isActive={isActive}
                        subject={route.subject}
                      />
                    </div>
                  )
                })}

                <NavigationItem
                  {...newClassRoute}
                  key="newClass"
                  isExpanded={isSideMenuExpanded}
                  isActive={checkUrlIsActive(newClassRoute, location)}
                  className={classNames(
                    'mx-auto border border-primary-600 bg-primary-800',
                    isSideMenuExpanded ? 'w-full flex-row-reverse justify-between pl-2' : 'h-9 w-9 rounded-full'
                  )}
                  iconClassName="size-4 text-primary-300"
                />

                <div className="group flex w-full flex-col gap-2 px-1">
                  <GradientDivider />

                  {/* Dictionaries */}
                  <div className="flex items-center justify-start gap-3 pb-2">
                    {/* <img src={ImageContent.books} className={classNames('size-10', !isSideMenuExpanded && 'mx-auto')} /> */}
                    <BookOpen01 className={classNames('size-5 text-primary-200', !isSideMenuExpanded && 'mx-auto')} />
                    {isSideMenuExpanded && (
                      <Body size="md" className="text-white">
                        {t('routes.dictionaries')}
                      </Body>
                    )}
                  </div>

                  {middleRoutes.map((route, index) => {
                    if (route.children) {
                      return (
                        <CollapsibleWrapper
                          key={index}
                          routes={route.children}
                          {...route}
                          isExpanded={isSideMenuExpanded}
                          isActive={checkUrlIsActive(route, location)}
                        />
                      )
                    } else {
                      return (
                        <NavigationItem
                          {...route}
                          key={index}
                          isExpanded={isSideMenuExpanded}
                          isActive={checkUrlIsActive(route, location)}
                          className="mx-auto flex w-full justify-center border-primary-600 bg-primary-800 p-1"
                          iconClassName="size-4"
                        />
                      )
                    }
                  })}
                </div>
              </div>
            </div>

            {/* Bottom routes */}
            <div className="relative flex w-full flex-col items-center px-4 pb-4">
              <div className="pointer-events-none absolute -top-10 left-0 z-0 h-10 w-full bg-gradient-to-b from-primary-1000/0 to-primary-1000" />

              <GradientDivider className="my-2" />

              <BugReportButton
                isExpanded={isSideMenuExpanded}
                onClick={() =>
                  isBugReportOpen ? dialogStateStore.setIsClosed('reportBug') : dialogStateStore.setIsOpen('reportBug')
                }
              />
              <UserInfo isExpanded={isSideMenuExpanded} />
            </div>
          </div>
        </nav>
      </aside>

      {isHidden && (
        <motion.div
          animate={{ opacity: isHovered ? 0 : 1 }}
          transition={{ duration: 0.1 }}
          className="absolute -right-[35px] top-1/2 z-10 -translate-y-1/2"
          onHoverStart={onHoverStart}
        >
          <HoverableIcon />
        </motion.div>
      )}

      {isBugReportOpen && <BugReportDialog onClose={() => dialogStateStore.setIsClosed('reportBug')} />}
    </motion.div>
  )
}
